<template>
    <div>
        <h4 v-if="title">{{ title }}</h4>

        <!-- Summary Stats -->
        <v-row v-if="showSummary && previewData && summaryCards" class="mb-4">
            <v-col v-for="card in summaryCards" :key="card.key" :cols="card.cols || 3">
                <v-card class="text-center pa-3">
                    <div class="text-h4">{{ getSummaryValue(card.key) }}</div>
                    <div>{{ card.label }}</div>
                </v-card>
            </v-col>
        </v-row>

        <!-- Controls: Search, Filter, Counts -->
        <div v-if="previewRows && previewRows.length > 0" class="mb-4">
            <div class="d-flex align-center flex-wrap" style="gap: 16px;">
                <v-text-field v-model="searchTerm" label="Search" prepend-inner-icon="mdi-magnify" clearable outlined
                    dense style="max-width: 360px;"></v-text-field>

                <v-select v-model="statusFilter" :items="statusFilterOptions" label="Filter by status" outlined dense
                    clearable style="max-width: 220px;"></v-select>

                <v-spacer></v-spacer>

                <div class="grey--text text--darken-1 text-caption">
                    Showing <strong>{{ filteredRows.length }}</strong> of <strong>{{ previewRows.length }}</strong>
                </div>
            </div>
        </div>

        <!-- Records Table -->
        <div v-if="previewRows && previewRows.length > 0">
            <v-data-table
                :headers="tableHeaders"
                :items="filteredRows"
                :items-per-page="itemsPerPage"
                :hide-default-footer="hideFooter"
                :search="searchTerm"
                class="elevation-1"
                :loading="loading"
                :dense="dense"
                :fixed-header="true"
            >
                <!-- Header slots for custom headers -->
                <template v-for="header in tableHeaders" :slot="`header.${header.value}`" slot-scope="{ header: headerData }">
                    <slot :name="`header-${header.value}`" :header="headerData">
                        {{ headerData.text }}
                    </slot>
                </template>

                <!-- Status Column -->
                <template slot="item.import_status" slot-scope="{ item }">
                    <v-chip :color="item.will_be_imported ? 'success' : 'red'" text-color="white" small>
                        {{ item.will_be_imported ? 'Can Import' : 'Cannot Import' }}
                    </v-chip>
                </template>

                <!-- Dynamic column slots -->
                <template v-for="column in customColumns" :slot="`item.${column.key}`" slot-scope="{ item }">
                    <slot :name="`column-${column.key}`" :item="item" :value="item[column.key]">
                        {{ formatColumnValue(item, column) }}
                    </slot>
                </template>

                <!-- System Notes Column -->
                <template slot="item.rc_system_notes" slot-scope="{ item }">
                    <slot name="system-notes" :item="item" :value="item.rc_system_notes">
                        <div v-if="item.rc_system_notes && item.rc_system_notes.trim()">
                            <ul style="margin: 0; padding-left: 16px;">
                                <li v-for="note in formatSystemNotes(item.rc_system_notes)" :key="note" style="margin-bottom: 4px;">
                                    {{ note }}
                                </li>
                            </ul>
                        </div>
                        <div v-else class="text-caption grey--text">
                            No notes
                        </div>
                    </slot>
                </template>

                <!-- Errors Column -->
                <template slot="item.errors" slot-scope="{ item }">
                    <div v-if="item.errors && item.errors.length > 0">
                        <ul style="margin: 0; padding-left: 16px;">
                            <li v-for="error in item.errors" :key="error" style="margin-bottom: 4px;">
                                {{ error }}
                            </li>
                        </ul>
                    </div>
                    <div v-else class="text-caption grey--text">
                        No errors
                    </div>
                </template>
            </v-data-table>
        </div>

        <!-- No Data Message -->
        <div v-else-if="!loading">
            <v-alert type="info" outlined>
                No preview data available.
            </v-alert>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ImportPreviewTable',

    props: {
        previewData: {
            type: Object,
            default: null
        },
        title: {
            type: String,
            default: ''
        },
        showSummary: {
            type: Boolean,
            default: true
        },
        summaryCards: {
            type: Array,
            default: () => []
        },
        customColumns: {
            type: Array,
            default: () => []
        },
        tableHeaders: {
            type: Array,
            required: true
        },
        itemsPerPage: {
            type: Number,
            default: 25
        },
        hideFooter: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        autoFilterToErrors: {
            type: Boolean,
            default: false
        },
        dense: {
            type: Boolean,
            default: false
        },
        tableHeight: {
            type: [Number, String],
            default: 520
        },
        showSelectionFilter: {
            type: Boolean,
            default: true
        },
        selectedRowNumbers: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            searchTerm: '',
            statusFilter: null,
            showOnlySelected: false,
            statusFilterOptions: [
                { text: 'Can Import', value: 'can_import' },
                { text: 'Cannot Import', value: 'error' }
            ]
        };
    },

    watch: {
        // Watch for changes to autoFilterToErrors prop and preview data
        autoFilterToErrors: {
            immediate: true,
            handler(newVal) {
                if (newVal && this.hasErrorRows) {
                    this.statusFilter = 'error';
                }
            }
        },

        // Watch for preview data changes to auto-filter if needed
        previewData: {
            immediate: true,
            handler() {
                if (this.autoFilterToErrors && this.hasErrorRows) {
                    this.statusFilter = 'error';
                }
            }
        }
    },

    computed: {
        previewRows() {
            return this.previewData?.preview_rows || [];
        },

        filteredRows() {
            let rows = this.previewRows;

            // Apply selection filter first if enabled
            if (this.showOnlySelected && this.selectedRowNumbers && this.selectedRowNumbers.length > 0) {
                const set = new Set(this.selectedRowNumbers);
                rows = rows.filter(row => set.has(row.row_number));
            }

            // Apply status filter
            if (this.statusFilter === 'can_import') {
                rows = rows.filter(row => row.will_be_imported);
            } else if (this.statusFilter === 'error') {
                rows = rows.filter(row => !row.will_be_imported);
            }

            return rows;
        },

        hasErrorRows() {
            if (!this.previewRows || this.previewRows.length === 0) {
                return false;
            }
            return this.previewRows.some(row => !row.will_be_imported);
        }
    },

    methods: {
        getSummaryValue(key) {
            return this.previewData?.[key] || 0;
        },

        formatColumnValue(item, column) {
            const value = item[column.key];

            if (column.formatter && typeof column.formatter === 'function') {
                return column.formatter(value, item);
            }

            if (column.type === 'boolean') {
                return value ? 'Yes' : 'No';
            }

            return value || '';
        },

        /**
         * Format system notes by splitting on comma delimiter
         */
        formatSystemNotes(notes) {
            if (!notes || !notes.includes(',')) {
                return [notes];
            }
            return notes.split(',').filter(note => note.trim().length > 0);
        },
    }
};
</script>
